import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mocv_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  getCurrentUser: async (token) => {
    const response = await api.get(`/auth/me?token=${token}`);
    return response.data;
  },
};

// CV API
export const cvAPI = {
  extractText: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/cv/extract-text', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  
  uploadPhoto: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  
  generate: async (data) => {
    const response = await api.post('/cv/generate', data);
    return response.data;
  },
  
  get: async (cvId) => {
    const response = await api.get(`/cv/${cvId}`);
    return response.data;
  },
  
  list: async (search = '') => {
    const response = await api.get(`/cvs${search ? `?search=${encodeURIComponent(search)}` : ''}`);
    return response.data;
  },
  
  delete: async (cvId) => {
    const response = await api.delete(`/cv/${cvId}`);
    return response.data;
  },
  
  update: async (cvId, data) => {
    const response = await api.put(`/cv/${cvId}`, data);
    return response.data;
  },
  
  translate: async (cvId, targetLang) => {
    const response = await api.post(`/cv/${cvId}/translate?target_lang=${targetLang}`);
    return response.data;
  },
  
  getVersions: async (cvId) => {
    const response = await api.get(`/cv/${cvId}/versions`);
    return response.data;
  },
  
  restoreVersion: async (cvId, versionId) => {
    const response = await api.post(`/cv/${cvId}/restore/${versionId}`);
    return response.data;
  },
  
  exportPDF: (cvId) => `${API_BASE}/cv/${cvId}/export/pdf`,
  exportPDFWatermark: (cvId) => `${API_BASE}/cv/${cvId}/export/pdf-watermark`,
  exportPNG: (cvId) => `${API_BASE}/cv/${cvId}/export/png`,
  exportDOCX: (cvId) => `${API_BASE}/cv/${cvId}/export/docx`,
};

// Cover Letter API
export const coverLetterAPI = {
  generate: async (data) => {
    const response = await api.post('/cover-letter/generate', data);
    return response.data;
  },
  
  get: async (letterId) => {
    const response = await api.get(`/cover-letter/${letterId}`);
    return response.data;
  },
  
  exportPDF: (letterId) => `${API_BASE}/cover-letter/${letterId}/export/pdf`,
  exportDOCX: (letterId) => `${API_BASE}/cover-letter/${letterId}/export/docx`,
};

// V2 Builder API - Completely isolated from V1
export const v2API = {
  // Templates
  getTemplates: async () => {
    const response = await api.get('/v2/templates');
    return response.data;
  },
  
  // CV CRUD
  generate: async (data) => {
    const response = await api.post('/v2/cv/generate', data);
    return response.data;
  },
  
  get: async (cvId) => {
    const response = await api.get(`/v2/cv/${cvId}`);
    return response.data;
  },
  
  list: async (search = '') => {
    const response = await api.get(`/v2/cvs${search ? `?search=${encodeURIComponent(search)}` : ''}`);
    return response.data;
  },
  
  update: async (cvId, data) => {
    const response = await api.put(`/v2/cv/${cvId}`, data);
    return response.data;
  },
  
  delete: async (cvId) => {
    const response = await api.delete(`/v2/cv/${cvId}`);
    return response.data;
  },
  
  updateVisibility: async (cvId, visibility) => {
    const response = await api.put(`/v2/cv/${cvId}/visibility`, visibility);
    return response.data;
  },
  
  // Generate Tasks
  generateTasks: async (jobTitle, company = '', industry = '') => {
    const response = await api.post('/v2/cv/generate-tasks', { jobTitle, company, industry });
    return response.data;
  },
  
  // Smart Edit
  smartEdit: async (cvId, data) => {
    const response = await api.post(`/v2/cv/${cvId}/smart-edit`, data);
    return response.data;
  },
  
  // Versions
  getVersions: async (cvId) => {
    const response = await api.get(`/v2/cv/${cvId}/versions`);
    return response.data;
  },
  
  restoreVersion: async (cvId, versionId) => {
    const response = await api.post(`/v2/cv/${cvId}/restore/${versionId}`);
    return response.data;
  },
  
  // Cover Letter
  generateCoverLetter: async (data) => {
    const response = await api.post('/v2/cover-letter/generate', data);
    return response.data;
  },
  
  // Exports
  exportPDFClean: (cvId) => `${API_BASE}/v2/cv/${cvId}/export/pdf-clean`,
  exportPDFWatermark: (cvId) => `${API_BASE}/v2/cv/${cvId}/export/pdf-watermark`,
  
  // QA (Admin)
  runQA: async (templateId, qaBy) => {
    const response = await api.post(`/v2/admin/qa/test-export?v2_template_id=${templateId}&qa_by=${qaBy}`);
    return response.data;
  },
  
  getQAResults: async () => {
    const response = await api.get('/v2/admin/qa/results');
    return response.data;
  },
};

// V3 Builder API
export const v3API = {
  // Templates
  getTemplates: async () => {
    const response = await api.get('/v3/templates');
    return response.data;
  },
  
  getSidebarColors: async () => {
    const response = await api.get('/v3/sidebar-colors');
    return response.data;
  },
  
  // CV CRUD
  createCV: async (data) => {
    const response = await api.post('/v3/cv/generate', data);
    return response.data;
  },
  
  getCVs: async (search = '') => {
    const response = await api.get(`/v3/cvs${search ? `?search=${search}` : ''}`);
    return response.data;
  },
  
  getCV: async (cvId) => {
    const response = await api.get(`/v3/cv/${cvId}`);
    return response.data;
  },
  
  updateCV: async (cvId, data) => {
    const response = await api.put(`/v3/cv/${cvId}`, data);
    return response.data;
  },
  
  deleteCV: async (cvId) => {
    const response = await api.delete(`/v3/cv/${cvId}`);
    return response.data;
  },
  
  // Section Layout
  updateSectionLayout: async (cvId, layout) => {
    const response = await api.put(`/v3/cv/${cvId}/section-layout`, layout);
    return response.data;
  },
  
  // Visibility
  updateVisibility: async (cvId, visibility) => {
    const response = await api.put(`/v3/cv/${cvId}/visibility`, visibility);
    return response.data;
  },
  
  // Photo
  uploadPhoto: async (cvId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/v3/cv/${cvId}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  // Generate Tasks (reuse from V2)
  generateTasks: async (jobTitle, company = '', industry = '') => {
    const response = await api.post('/v2/cv/generate-tasks', { jobTitle, company, industry });
    return response.data;
  },
  
  // Smart Edit
  smartEdit: async (cvId, data) => {
    const response = await api.post(`/v3/cv/${cvId}/smart-edit`, data);
    return response.data;
  },
  
  // Version History
  getVersions: async (cvId) => {
    const response = await api.get(`/v3/cv/${cvId}/versions`);
    return response.data;
  },
  
  restoreVersion: async (cvId, versionId) => {
    const response = await api.post(`/v3/cv/${cvId}/restore/${versionId}`);
    return response.data;
  },
  
  deleteVersion: async (cvId, versionId) => {
    const response = await api.delete(`/v3/cv/${cvId}/versions/${versionId}`);
    return response.data;
  },
  
  // QA (Admin)
  runQA: async (templateId, qaBy) => {
    const response = await api.post(`/v3/admin/qa/test-export?v3_template_id=${templateId}&qa_by=${qaBy}`);
    return response.data;
  },
  
  getQAResults: async () => {
    const response = await api.get('/v3/admin/qa/results');
    return response.data;
  },
  
  // Exports
  exportPDFClean: (cvId) => `${API_BASE}/v3/cv/${cvId}/export/pdf-clean`,
  exportPDFWatermark: (cvId) => `${API_BASE}/v3/cv/${cvId}/export/pdf-watermark`,
  getPreviewUrl: (cvId) => `${API_BASE}/v3/cv/${cvId}/preview`,
  
  // Cover Letters
  generateCoverLetter: async (data) => {
    const response = await api.post('/v3/cover-letter/generate', data);
    return response.data;
  },
  
  getCoverLetters: async (cvId) => {
    const response = await api.get(`/v3/cover-letters/${cvId}`);
    return response.data;
  },
  
  getCoverLetter: async (letterId) => {
    const response = await api.get(`/v3/cover-letter/${letterId}`);
    return response.data;
  },
  
  updateCoverLetter: async (letterId, data) => {
    const response = await api.put(`/v3/cover-letter/${letterId}`, data);
    return response.data;
  },
  
  deleteCoverLetter: async (letterId) => {
    const response = await api.delete(`/v3/cover-letter/${letterId}`);
    return response.data;
  },
  
  exportCoverLetterPDF: (letterId) => `${API_BASE}/v3/cover-letter/${letterId}/export/pdf`,
  exportCoverLetterPDFWatermark: (letterId) => `${API_BASE}/v3/cover-letter/${letterId}/export/pdf-watermark`,
  getCoverLetterPreviewUrl: (letterId) => `${API_BASE}/v3/cover-letter/${letterId}/preview`,
};

// Admin API (Superadmin only)
export const adminAPI = {
  // Users
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  
  createUser: async (userData) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },
  
  getUser: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },
  
  updateUser: async (userId, userData) => {
    const response = await api.put(`/admin/users/${userId}`, userData);
    return response.data;
  },
  
  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },
  
  resetPassword: async (userId, newPassword) => {
    const response = await api.post(`/admin/users/${userId}/reset-password`, { newPassword });
    return response.data;
  },
  
  toggleUserStatus: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/status`);
    return response.data;
  },
  
  // Stats
  getCVStats: async () => {
    const response = await api.get('/admin/stats/cv-creation');
    return response.data;
  },
};

export default api;
